import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { ReferenceArea } from '../../models/reference-area';
import { Observable, of } from 'rxjs';
import { UserProfileService } from '../../../users/services/user-profile.service';
import { environment } from '../../../../../environments/environment';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { Project } from '../../../project/models/project';
import { map } from 'rxjs/operators';
import { FormValidationService } from '../../../../shared/services/form-validation.service';
import { ValidateUniqueField } from '../../../../shared/validators/uniqueField.validator';
import { ValidateInputLength } from '../../../../shared/validators/inputLength.validator';
import { TranslateService } from '@ngx-translate/core';
import { ReferenceAreaEditFormService } from '../../services/reference-area-edit-form.service';
@Component({
  selector: 'app-reference-area-edit-form',
  template: require('./reference-area-edit-form.component.html'),
  styles: [require('./reference-area-edit-form.component.sass')]
})
export class ReferenceAreaEditFormComponent implements OnInit, OnDestroy {

  @Input() referenceArea: ReferenceArea;
  @Input() projectId: number;
  @Input() editEnabled: boolean = true;
  @Input() referenceAreas: ReferenceArea[] | null;
  @Input() isSingleTrain: boolean = false;
  @Input() isShortMap: boolean = false;


  @Output() save = new EventEmitter<ReferenceArea>();
  @Output() delete = new EventEmitter<ReferenceArea>();
  @Output() cancel = new EventEmitter();
  
  referenceAreaForm: FormGroup;
  types: { label: string; code: string}[];
  projects$: Observable<Project[]>;

  get project() { return this.referenceAreaForm.get('projectId'); }
  get name() { return this.referenceAreaForm.get('name'); }
  get description() { return this.referenceAreaForm.get('description'); }
  get abbreviation() { return this.referenceAreaForm.get('abbreviation'); }
  get type() { return this.referenceAreaForm.get('type'); }
  get latLongList() { return this.referenceAreaForm.get('latLongList'); }
  get stationCode() { return this.referenceAreaForm.get('stationCode'); }

  constructor(
    private referenceAreaEditFormService: ReferenceAreaEditFormService,
    public userProfileService: UserProfileService,
    private projectService: ProjectDataService,
    public formValidation: FormValidationService,
    private translateService: TranslateService

) {
  }

  ngOnInit(): void {
    const otherReferenceAreas = this.referenceAreas ? this.referenceAreas.filter(referenceArea => this.referenceArea && this.referenceArea.id ? (referenceArea.id !== this.referenceArea.id) : true)  : [];
    this.referenceAreaForm = this.referenceAreaEditFormService.generate(otherReferenceAreas, [...environment.config.referenceAreas.types]);

    this.projects$ = this.projectService.getAllProjects().pipe(
      map(projects => projects.filter(project => project.id > 0))
    );

    this.referenceAreaForm.patchValue(this.referenceArea ? this.referenceArea : {
      projectId: this.projectId > 0 ? this.projectId : null
    });
    
    if (this.editEnabled) {
      this.referenceAreaForm.enable();
      if (this.referenceArea && this.referenceArea.id) {
        this.referenceAreaForm.get('type')?.disable({onlySelf: true});
        this.referenceAreaForm.get('stationCode')?.disable({onlySelf: true});
      }  
    } else {
      this.referenceAreaForm.disable();
    }

    if (this.projectId > 0 || (this.referenceArea && this.referenceArea.projectId > 0)) {
      this.referenceAreaForm.get('projectId')?.disable({onlySelf: true});
    }

    this.types = environment.config.referenceAreas.types.map(type => {
      return {
        label: this.translateService.instant(`I18N_REFERENCE_AREAS.${type}`),
        code: type
    }})
  }

  enableForm() {
    this.editEnabled = true;
    this.referenceAreaForm.enable();
    if (this.referenceArea && this.referenceArea.id) {
      this.referenceAreaForm.get('type')?.disable({onlySelf: true});
      this.referenceAreaForm.get('stationCode')?.disable({onlySelf: true});
    }
    if (this.projectId > 0 || (this.referenceArea && this.referenceArea.projectId > 0)) {
      this.referenceAreaForm.get('projectId')?.disable({onlySelf: true});
    }
  }

  onSave = () => {
    if(this.referenceAreaForm.valid){
      const positions = this.referenceAreaForm.value.latLongList.split(" ");
      const referenceArea = {
        id: this.referenceArea && this.referenceArea.id,
        name: this.referenceAreaForm.value.name,
        description: this.referenceAreaForm.value.description,
        stationCode: this.referenceArea && this.referenceArea.id ? this.referenceArea.stationCode : this.referenceAreaForm.value.stationCode,
        abbreviation: this.referenceAreaForm.value.abbreviation,
        type: this.referenceArea && this.referenceArea.id ? this.referenceArea.typeCode : this.referenceAreaForm.value.type,
        area: {
          positions: positions.map(position => {
            const latLong = position.split(",");
            return {
              latitude: latLong[0],
              longitude: latLong[1]
            }
          })
        },
        projectId:  this.referenceArea && this.referenceArea.id ? this.referenceArea.projectId : (this.projectId > 0 ? this.projectId : this.referenceAreaForm.value.projectId) 
      }
      this.save.emit(referenceArea as ReferenceArea)
    }
      else{
        this.formValidation.validateAllFormFields(this.referenceAreaForm);
      }
    }

  onCancel = () => {
    this.cancel.emit();
  }

  onDelete = () => {
    this.delete.emit(this.referenceArea);
  }

  ngOnDestroy(): void {
    this.referenceAreaForm.reset();
  }
}
  /**
 * Validate an area is entered in the correct format.
 * A minimum of 3 geo-positions are required.
 * A geo-position will be a string: latitude,longitude
 * geo-positions should contain a single space between
 * Example:
 * 48.858093,2.294694 48.857000,2.297000 48.857000,2.291000
 */
export function areaValidator(positionLength: number): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    const area: string = control.value;
    const positions = area && area.split(" ");

    if (positions) {
      if (positions.length < positionLength) {
        return {
          minPositions: positionLength,
          actualPositions: positions.length
        };
      } 
  
      for (let position of positions) {
        const positionSplit = position.split(",");
  
        if (positionSplit.length != 2) {
          return {cannotParsePosition: position};
        } else {
  
          if (positionSplit[0] === "") {
            return {latitudeMissing: position};
          }
          if (positionSplit[1] === "") {
            return {longitudeMissing: position};
          }
  
          const latitude = parseInt(positionSplit[0]);
          const longitude = parseInt(positionSplit[1]);
  
          if (isNaN(latitude) || (latitude < -90 || latitude > 90)) {
            const latitudeVal = isNaN(latitude) ? positionSplit[0] : latitude;
            return {latitudeInvalid: latitudeVal};
          }
  
          if (isNaN(longitude) || (longitude < -180 || longitude > 180)) {
            const longitudeVal = isNaN(longitude) ? positionSplit[1] : longitude;
            return {longitudeInvalid: longitudeVal};
          }
        }
      }
      return null;
    } else {
      return null
    }
  }
}
